/**
 * The bench's fighting pilot (`?autopilot=1`): faces the opponent, strafes, fires in range, uses
 * the ability every eight seconds and the ultimate when charged, vents above 80 % heat. It reads
 * the match state like the HUD does and produces an ordinary input sample.
 */
import { aimOrigin, centerOf, type MatchState, type Slot } from '@steelwake/shared/combat';
import { BALANCE, getMech, getWeapon } from '@steelwake/shared/data';
import { pitchOf, yawOf } from '@steelwake/shared/sim';

import type { InputSample } from '../sim/input.js';

export function fighterSample(state: MatchState, slot: Slot, timeSeconds: number): InputSample {
  const me = state.mechs[slot];
  const enemy = state.mechs[slot === 0 ? 1 : 0];
  const def = getMech(me.mechId);
  const enemyDef = getMech(enemy.mechId);
  const weapon = getWeapon(def.weapons[0]!.id);
  const from = aimOrigin(me.body, def, BALANCE.aimHeightFraction);
  const to = centerOf(enemy.body, enemyDef);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const dist = Math.hypot(dx, dy, dz);
  const t = timeSeconds;
  const optimal = weapon.kind === 'melee' ? (weapon.melee?.reach ?? 3) * 0.8 : weapon.range.optimal;
  const inRange =
    weapon.kind === 'melee' ? dist <= (weapon.melee?.reach ?? 3) + 0.5 : dist <= weapon.range.max;
  const closing = dist > optimal * 1.1;
  return {
    moveX: Math.sin(t / 1.6) > 0 ? 1 : -1,
    moveY: closing ? 1 : dist < optimal * 0.6 ? -1 : 0,
    aimYaw: yawOf(dx, dz) + Math.sin(t * 2.3) * 0.01,
    aimPitch: pitchOf(dx, dy, dz),
    boost: closing && t % 6 < 2,
    jump: t % 4 < 0.1,
    primary: inRange && enemy.alive,
    secondary: false,
    ability: t % 8 < 0.05,
    ultimate: me.ultCharge >= 100,
    vent: me.heat > def.heatCap * 0.8,
    melee: dist < 2.9,
  };
}
